import {BaseStore, globalStore, StoreClass, StoreObject} from "../../../stem-core/state/Store";
import {field} from "../../../stem-core/state/StoreField";
import {type StemDate} from "../../../stem-core/time/Date";
import {apiClient} from "../../connection/BlinkApiClient";
import {BaseEnum, makeEnum} from "../../../stem-core/state/BaseEnum";
import {BlinkGlobal} from "../../../blinkpay/UtilsLib";
import {filterStoreByMerchantAndLooseIdArray} from "../misc/Filter";
import {Batcher} from "../../../stem-core/base/Batcher";
import {VisibilityStatus} from "../misc/GenericEnums";
import {Merchant} from "../MerchantStore";

@makeEnum
export class AudienceNewsletterFrequency extends BaseEnum {
    static DAILY;
    static WEEKLY;
    static MONTHLY;
    static CUSTOM;
}

@globalStore
export class MerchantAudience extends BaseStore("MerchantAudience") {
    @field(Number) merchantId: number;
    @field("Merchant") merchant: Merchant;
    @field(String) name: string;
    @field(VisibilityStatus) visibility: VisibilityStatus;
    @field(Boolean) isPublic: boolean;
    @field(AudienceNewsletterFrequency) defaultFrequency: AudienceNewsletterFrequency;
    @field(Array) defaultFrequencyOffset: number[];
    @field(Date) defaultSendTime: StemDate;
    @field(Boolean) autosubscribeNewUsers: boolean;
    @field(Array) autosubscribeOnBenefitIds: string[];
    size?: number;

    toString(): string {
        return this.name;
    }

    getSize(): number | null {
        if (this.size >= 0) {
            // To protect from the -1 used as sentinel
            return this.size;
        }
        return null;
    }

    static statsBatcher = new Batcher(async (audiences: MerchantAudience[]) => {
        const response = await apiMerchantGetAudienceStats({audienceIds: audiences.map(audience => audience.id)});
        audiences.forEach((audience) => {
            Object.assign(audience, response[audience.id]);
            audience.dispatchChange();
        })
    });

    static allUnfiltered(): MerchantAudience[] {
        return super.all<MerchantAudience>();
    }

    static all<T extends StoreObject>(this: StoreClass<T>): T[] {
        return filterStoreByMerchantAndLooseIdArray(super.all<MerchantAudience>(), BlinkGlobal.iFrameState?.newsletter,
            (audience, idOrName) => audience.id == idOrName || audience.name == idOrName) as any;
    }

    static enqueueLoadStats(audience: MerchantAudience): void {
        if (audience.size != null) {
            return;
        }
        audience.size = -1; // Mark that we've enqueued this audience
        this.statsBatcher.process(audience);
    }
}

export async function apiMerchantCreateAudience(request: any): Promise<any> {
    return apiClient.post("/merchant/create_audience", request);
}

export async function apiMerchantEditAudience(request: any): Promise<any> {
    return apiClient.post("/merchant/edit_audience", request, {allowNulls: true});
}

export async function apiMerchantDeleteAudience(request: any): Promise<any> {
    return apiClient.post("/merchant/delete_audience", request);
}

export async function apiMerchantGetAudienceMembers(request: any): Promise<any> {
    return apiClient.get("/merchant/get_audience_members", request);
}

export async function apiMerchantGetAudienceStats(request: any): Promise<Record<string, any>> {
    return apiClient.get("/merchant/get_audience_stats", request);
}
